<script setup lang="tsx">
import { onBeforeMount, ref, watch } from 'vue'
import Multiselect from 'vue-multiselect'
import type { Responsible, Response } from '@/models/api-generated'
import http from '@/services/http'
import { getResponsibleIcon } from '@/services/utils'
import { sharedFetch } from './dataSource'

const props = withDefaults(
	defineProps<{
		id?: string
		class?: string
		placeholder?: string
		inputId?: string
		dataTest?: string
		hide?: Responsible[]
	}>(),
	{
		id: '',
		class: '',
		placeholder: '',
		inputId: '',
		dataTest: '',
	},
)
const isLoading = ref(false)
const model = defineModel<Responsible | undefined>({ required: true })
const options = ref<Responsible[]>([])

onBeforeMount(() => search(''))

async function search(q: string) {
	isLoading.value = true

	const data = await sharedFetch('responsible', async () => {
		const { data } = await http.get<Response<Responsible[]>>('api/responsibles?showActive=true&q=' + q)
		return data
	})

	options.value = hideMe(data.data ?? [])
	shiftSelected()

	isLoading.value = false
}

watch(model, shiftSelected)

function shiftSelected() {
	if (model.value) {
		const idx = options.value.findIndex((x) => x.trackBy === model.value?.trackBy)
		if (idx >= 0) {
			const selected = options.value.splice(idx, 1)
			options.value.unshift(...selected)
		}
	}
}

function hideMe(options: Responsible[]) {
	props.hide?.forEach((x) => {
		const idx = options.findIndex((y) => x.trackBy === y.trackBy)
		if (idx >= 0) options.splice(idx, 1)
	})
	return options
}
</script>

<template>
	<Multiselect
		:data-test="props.dataTest"
		:id="props.id"
		:class="props.class"
		:placeholder="props.placeholder"
		:multiple="false"
		:show-labels="false"
		:use-teleport="true"
		:hide-selected="false"
		:options="options"
		:internal-search="false"
		:preserve-search="true"
		:show-no-results="false"
		:loading="isLoading"
		v-model="model"
		label="label"
		track-by="trackBy"
		@search-change="search"
	>
		<template #option="{ option }">
			<div><i :class="getResponsibleIcon(option)"></i>{{ option.label }}</div>
		</template>

		<template #tag="{ option, remove }">
			<span class="multiselect__tag" :class="{ 'bg-success': option.type === 'roles' }">
				<span>{{ option.label }}</span>
				<i class="multiselect__tag-icon" @mousedown.prevent.stop="remove(option)"></i>
			</span>
		</template>
		<template #singleLabel="{ option }">
			<span class="d-inline-flex align-items-center">
				<i class="bi bi-x-lg me-2" @mousedown.prevent.stop="model = undefined" style="cursor: pointer"></i>
				<i :class="getResponsibleIcon(option)" class="me-1"></i>
				<span>{{ option.label }}</span>
			</span>
		</template>
	</Multiselect>

	<template v-if="model">
		<input type="hidden" :name="'responsible_id' + props.inputId" :value="model.id" />
		<input type="hidden" :name="'responsible_type' + props.inputId" :value="model.type" />
	</template>
</template>

<style lang="sass" scoped>
:deep(.multiselect__option--selected)
  background-color: #d1e3f1 !important
  color: #000 !important

:deep(.multiselect__content-wrapper)
  position: absolute !important
  z-index: 9999 !important
</style>
